/**
 * Shared editor primitives — verbatim ports of the prototype's SvcRow /
 * SvcSeg / SvcSelect / SvcNumField / SvcTagField / SvcSectionHead /
 * ExceptionList / ScheduleGrid helpers, used by Location, Staff, and
 * Resource editors.
 */
import { useState, type CSSProperties, type ReactNode } from "react";
import { Chip, IconBtn, alpha, Btn, Icon, SegmentedControl, T } from "@v0y4ge/jrni-ui";
import { useT, t as i18nT, formatICU } from "./i18n";

/* ── Booking channels (events / appointments / queues) ───────────────────── */
export const SVC_CHANNELS = [
  { id: "events",       labelKey: "lib.svcEditorPrimitives.events", get label() { return i18nT("lib.svcEditorPrimitives.events", "Events"); },       icon: "calendar", hue: T.p1 },
  { id: "appointments", labelKey: "lib.svcEditorPrimitives.appointments", get label() { return i18nT("lib.svcEditorPrimitives.appointments", "Appointments"); }, icon: "clock",    hue: "#1F8A5B" },
  { id: "queues",       labelKey: "lib.svcEditorPrimitives.queues", get label() { return i18nT("lib.svcEditorPrimitives.queues", "Queues"); },       icon: "list",     hue: "#C77F14" },
] as const;
export type ChannelId = "events" | "appointments" | "queues";
export const SVC_CHANNEL = (id: string) =>
  SVC_CHANNELS.find((c) => c.id === id) ?? SVC_CHANNELS[0];

export const WEEKDAYS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"] as const;

/* ── Layout helpers ─────────────────────────────────────────────────────── */
export function SvcSectionHead({ icon, title, desc }: { icon: ReactNode; title: string; desc?: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 13, marginBottom: 4 }}>
      <span style={{ width: 38, height: 38, borderRadius: 10, background: T.p3, color: T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{icon}</span>
      <div>
        <div style={{ fontSize: 18, fontWeight: 700, color: T.n1, letterSpacing: -0.3 }}>{title}</div>
        {desc ? <div style={{ fontSize: 13, color: T.soft }}>{desc}</div> : null}
      </div>
    </div>
  );
}

export function SvcRow({ label, hint, full, children }: { label: string; hint?: string; full?: boolean; children: ReactNode }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: full ? "1fr" : "210px 1fr", gap: full ? 8 : 18, padding: "16px 0", borderTop: `1px solid ${T.border}`, alignItems: full ? "stretch" : "start" }}>
      <div>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: T.n1 }}>{label}</div>
        {hint ? <div style={{ fontSize: 12, color: T.soft, marginTop: 3, lineHeight: 1.4 }}>{hint}</div> : null}
      </div>
      <div style={{ minWidth: 0 }}>{children}</div>
    </div>
  );
}

/* ── Controls ───────────────────────────────────────────────────────────── */
export function SvcSeg({ value, onChange, options, disabled, ariaLabel }: { value: string; onChange: (v: string) => void; options: { id: string; label: string }[]; disabled?: boolean; ariaLabel?: string }) {
  /* The DS `SegmentedControl` under this file's historic `{ id, label }`
     signature (adopted 2026-08-11). jrni-ui already ships this control with the
     same contract — options / value / onChange — PLUS what the hand-roll never
     had: `role="radiogroup"` + `role="radio"`/`aria-checked` semantics, a size
     scale, and per-option `disabled` (the group-level flag maps straight onto
     it). The old body painted the selected segment a `T.p1` fill with a raw
     `T.white` ink — BOTH halves invert in dark, so the selected segment went
     dark-on-dark; the DS active chip is a `T.p3` tint with `T.p1` ink and reads
     in both themes. `ariaLabel` is optional and unset by default, so existing
     call sites are untouched. */
  return (
    <SegmentedControl
      value={value}
      onChange={onChange}
      ariaLabel={ariaLabel}
      options={options.map((o) => ({ value: o.id, label: o.label, disabled }))}
    />
  );
}

export function SvcSelect({ value, onChange, options, disabled, style }: { value: string; onChange?: (v: string) => void; options: { id: string; label: string }[]; disabled?: boolean; style?: CSSProperties }) {
  return (
    <div style={{ position: "relative", display: "inline-flex", width: "100%" }}>
      <select value={value} disabled={disabled} onChange={(e) => onChange?.(e.target.value)}
        style={{ width: "100%", height: 40, padding: "0 34px 0 12px", border: `1px solid ${T.n4}`, borderRadius: 8, background: T.white, color: T.n1, fontSize: 14, fontFamily: T.font, cursor: disabled ? "default" : "pointer", appearance: "none", ...style }}>
        {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
      <span style={{ position: "absolute", right: 11, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: T.soft }}><Icon.chev size={14} /></span>
    </div>
  );
}

export function SvcNumField({ value, onChange, min = 0, max = 100000, step = 1, unit, prefix, width = 110, disabled }: { value: number; onChange: (v: number) => void; min?: number; max?: number; step?: number; unit?: string; prefix?: string; width?: number; disabled?: boolean }) {
  return (
    <div style={{ display: "inline-flex", alignItems: "center", height: 40, border: `1px solid ${T.n4}`, borderRadius: 8, background: disabled ? T.n6 : T.white, overflow: "hidden", width }}>
      {prefix ? <span style={{ paddingLeft: 11, color: T.soft, fontSize: 14 }}>{prefix}</span> : null}
      <input type="number" value={value} min={min} max={max} step={step} disabled={disabled}
        onChange={(e) => { const v = e.target.value === "" ? 0 : Number(e.target.value); onChange(Math.max(min, Math.min(max, v))); }}
        style={{ width: "100%", height: "100%", border: "none", outline: "none", background: "transparent", padding: prefix ? "0 6px 0 4px" : "0 6px 0 11px", fontSize: 14, fontFamily: T.font, color: T.n1 }} />
      {unit ? <span style={{ paddingRight: 11, color: T.soft, fontSize: 12.5, whiteSpace: "nowrap" }}>{unit}</span> : null}
    </div>
  );
}

export function SvcTagField({ tags, onChange, placeholder = "Add and press Enter", disabled }: { tags: string[]; onChange: (t: string[]) => void; placeholder?: string; disabled?: boolean }) {
  const [draft, setDraft] = useState("");
  const add = () => { const v = draft.trim(); if (v && !tags.includes(v)) onChange([...tags, v]); setDraft(""); };
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: 6, minHeight: 40, border: `1px solid ${T.n4}`, borderRadius: 8, background: disabled ? T.n6 : T.white, alignItems: "center" }}>
      {tags.map((t) => (
        <span key={t} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 6px 3px 10px", borderRadius: 999, background: T.p4, color: T.p1, fontSize: 12.5, fontWeight: 600 }}>
          {/* ui-raw: inline tag-chip remove glyph — TagInput/Chip owns this (group 6) */}
          {t}{!disabled ? <button onClick={() => onChange(tags.filter((x) => x !== t))} style={{ border: "none", background: "transparent", color: T.p1, cursor: "pointer", display: "inline-flex", padding: 0 }}><Icon.x size={12} /></button> : null}
        </span>
      ))}
      {!disabled ? <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={tags.length ? "" : placeholder}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); } else if (e.key === "Backspace" && !draft && tags.length) onChange(tags.slice(0, -1)); }}
        onBlur={add} style={{ flex: 1, minWidth: 90, border: "none", outline: "none", background: "transparent", fontSize: 13.5, fontFamily: T.font, color: T.n1, height: 26 }} /> : null}
    </div>
  );
}

/* ── ExceptionList ──────────────────────────────────────────────────────── */
export type ExceptionItem = { id?: string; kind: "block" | "extra"; label: string; from: string; to: string };
const exInput: CSSProperties = { height: 36, border: `1px solid ${T.n4}`, borderRadius: 7, padding: "0 8px", fontSize: 13, fontFamily: T.font, color: T.n1, background: T.white };

export function ExceptionList({ items, onChange, canWrite, allowExtra = true }: { items: ExceptionItem[]; onChange: (items: ExceptionItem[]) => void; canWrite: boolean; allowExtra?: boolean }) {
  const t = useT();
  const translate = t;
  const list = items || [];
  const setRow = (i: number, patch: Partial<ExceptionItem>) => onChange(list.map((t, idx) => idx === i ? { ...t, ...patch } : t));
  const rm = (i: number) => onChange(list.filter((_, idx) => idx !== i));
  const add = () => onChange([...list, { id: "ex_" + Date.now().toString(36), kind: "block", label: "", from: "", to: "" }]);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
      {list.length === 0 ? <div style={{ fontSize: 13, color: T.soft, padding: "6px 0" }}>{t("lib.svcEditorPrimitives.noExceptions", "No exceptions.")}</div> : list.map((t, i) => (
        <div key={t.id ?? i} style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, padding: "8px 10px", border: `1px solid ${t.kind === "extra" ? T.green2 : T.border}`, borderLeft: `3px solid ${t.kind === "extra" ? T.green1 : T.orange1}`, borderRadius: 9, background: T.white }}>
          {allowExtra ? (
            <select value={t.kind} disabled={!canWrite} onChange={(e) => setRow(i, { kind: e.target.value as "block" | "extra" })} style={{ ...exInput, fontWeight: 600, color: t.kind === "extra" ? T.green1 : "#8A5A12" }}>
              <option value="block">{translate("lib.svcEditorPrimitives.blockOff", "Block off")}</option>
              <option value="extra">{translate("lib.svcEditorPrimitives.extraAvailability", "Extra availability")}</option>
            </select>
          ) : null}
          <input value={t.label} onChange={(e) => setRow(i, { label: e.target.value })} disabled={!canWrite} placeholder={t.kind === "extra" ? translate("components.svcEditorPrimitives.reasonEGPopUpSaturday", "Reason (e.g. Pop-up Saturday)") : translate("components.svcEditorPrimitives.reasonEGVacation", "Reason (e.g. Vacation)")} style={{ ...exInput, flex: 1, minWidth: 140 }} />
          <input type="date" value={t.from} onChange={(e) => setRow(i, { from: e.target.value })} disabled={!canWrite} style={exInput} />
          <span style={{ color: T.soft, fontSize: 13 }}>→</span>
          <input type="date" value={t.to} onChange={(e) => setRow(i, { to: e.target.value })} disabled={!canWrite} style={exInput} />
          {t.from && !t.to ? <span style={{ fontSize: 11, color: "#8A5A12", fontWeight: 600 }}>{translate("lib.svcEditorPrimitives.ongoing", "⚠ ongoing")}</span> : null}
          {canWrite ? <IconBtn size={32} onClick={() => rm(i)}><Icon.x size={14} /></IconBtn> : null}
        </div>
      ))}
      {canWrite ? <div><Btn variant="secondary" size="sm" icon={<Icon.plus size={13} />} onClick={add}>{t("lib.svcEditorPrimitives.addException", "Add exception")}</Btn></div> : null}
    </div>
  );
}

/* ── ScheduleGrid ───────────────────────────────────────────────────────── */
export type TimeRange = { start: string; end: string };
export type WeeklyHours = Record<string, TimeRange[]>;
const ti: CSSProperties = { height: 34, border: `1px solid ${T.n4}`, borderRadius: 7, padding: "0 8px", fontSize: 13, fontFamily: T.font, color: T.n1, background: T.white, outline: "none" };

/**
 * Bulk fill for ScheduleGrid (2026-10-16, Pat: "mehrere Tage anwählen für das
 * Erfassen"). Pick any days (or Mon–Fri / Weekend / Every day), one time range,
 * Apply: those days' hours are REPLACED by that range in one step instead of
 * editing each day row. Local edit only — the host form's Save persists it,
 * exactly like a per-row edit. Opt-in via ScheduleGrid's `bulk` prop.
 */
function ScheduleBulkFill({ schedule, onChange }: { schedule: WeeklyHours; onChange: (s: WeeklyHours) => void }) {
  const t = useT();
  const [days, setDays] = useState<string[]>(["Mon", "Tue", "Wed", "Thu", "Fri"]);
  const [start, setStart] = useState("09:00");
  const [end, setEnd] = useState("17:00");
  const bad = !(end > start);
  const presets: { id: string; label: string; set: string[] }[] = [
    { id: "wd", label: t("lib.svcEditorPrimitives.presetWeekdays", "Mon – Fri"), set: ["Mon", "Tue", "Wed", "Thu", "Fri"] },
    { id: "we", label: t("lib.svcEditorPrimitives.presetWeekend", "Weekend"), set: ["Sat", "Sun"] },
    { id: "all", label: t("lib.svcEditorPrimitives.presetEveryDay", "Every day"), set: [...WEEKDAYS] },
  ];
  const same = (a: string[], b: string[]) => a.length === b.length && a.every((d) => b.includes(d));
  const toggle = (d: string) => setDays((cur) => (cur.includes(d) ? cur.filter((x) => x !== d) : WEEKDAYS.filter((x) => x === d || cur.includes(x))));
  const apply = () => { // ui-local: edits the form draft; the editor's Save persists
    const next: WeeklyHours = { ...schedule };
    days.forEach((d) => { next[d] = [{ start, end }]; });
    onChange(next);
  };
  const lab: CSSProperties = { fontSize: 12, fontWeight: 600, color: T.soft };
  return (
    <div data-testid="schedule-bulk-fill" style={{ border: `1px solid ${T.n5}`, borderRadius: 12, background: T.cardSoft, padding: 16, display: "flex", flexDirection: "column", gap: 14, marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: T.n1 }}>{t("lib.svcEditorPrimitives.setSeveralDays", "Set several days at once")}</span>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {presets.map((p) => <Chip key={p.id} selected={same(days, p.set)} onClick={() => setDays(p.set)}>{p.label}</Chip>)}
        </div>
      </div>
      <div role="group" aria-label={t("lib.svcEditorPrimitives.days", "Days")} style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {WEEKDAYS.map((d) => { const on = days.includes(d); return (
          <button key={d} type="button" aria-pressed={on} onClick={() => toggle(d)} data-testid={"schedule-bulk-day-" + d}
            style={{ width: 44, height: 44, borderRadius: 999, border: `1px solid ${on ? T.p1 : T.n5}`, background: on ? T.brandFill : T.white, color: on ? T.onColor : T.n2, fontWeight: 600, fontSize: 12.5, cursor: "pointer", fontFamily: T.font }}>
            {d}
          </button>
        ); })}
      </div>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}><span style={lab}>{t("lib.svcEditorPrimitives.from", "From")}</span>
          <input type="time" value={start} onChange={(e) => setStart(e.target.value)} style={ti} data-testid="schedule-bulk-from" /></label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}><span style={lab}>{t("lib.svcEditorPrimitives.to", "To")}</span>
          <input type="time" value={end} onChange={(e) => setEnd(e.target.value)} style={ti} data-testid="schedule-bulk-to" /></label>
        <span style={{ flex: 1, minWidth: 8 }} />
        {bad ? <span style={{ fontSize: 12, color: T.red1 }}>{t("lib.svcEditorPrimitives.endAfterStart", "End must be after start.")}</span> : null}
        <Btn size="sm" disabled={bad || !days.length} onClick={apply} testid="schedule-bulk-apply">
          {days.length ? formatICU(t("lib.svcEditorPrimitives.applyToDays", "Apply to {count, plural, one {# day} other {# days}}"), { count: days.length }) : t("lib.svcEditorPrimitives.pickADay", "Pick a day")}
        </Btn>
      </div>
    </div>
  );
}

export function ScheduleGrid({ schedule, onChange, canWrite, bulk }: { schedule: WeeklyHours; onChange: (s: WeeklyHours) => void; canWrite: boolean; /** Show the several-days fill bar above the rows. */ bulk?: boolean }) {
  const t = useT();
  const setDay = (day: string, ranges: TimeRange[]) => onChange({ ...schedule, [day]: ranges });
  const addRange = (day: string) => setDay(day, [...(schedule[day] || []), { start: "09:00", end: "17:00" }]);
  const rmRange = (day: string, idx: number) => setDay(day, (schedule[day] || []).filter((_, i) => i !== idx));
  const setRange = (day: string, idx: number, field: "start" | "end", v: string) =>
    setDay(day, (schedule[day] || []).map((r, i) => i === idx ? { ...r, [field]: v } : r));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {bulk && canWrite ? <ScheduleBulkFill schedule={schedule} onChange={onChange} /> : null}
      {WEEKDAYS.map((day) => {
        const ranges = schedule[day] || [];
        const on = ranges.length > 0;
        return (
          <div key={day} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "6px 0" }}>
            {/* ui-raw: day on/off toggle in a weekly-hours grid — WeeklyHours/ChipToggleGroup owns this (groups 6 and 9) */}
            <button type="button" disabled={!canWrite} onClick={() => on ? setDay(day, []) : addRange(day)}
              style={{ width: 72, flexShrink: 0, padding: "7px 0", borderRadius: 7, border: `1px solid ${on ? T.p2 : T.border}`, background: on ? T.p4 : T.n6, color: on ? T.p1 : T.soft, fontSize: 12.5, fontWeight: 700, cursor: canWrite ? "pointer" : "default", fontFamily: T.font, textAlign: "center" }}>
              {day}
            </button>
            <div style={{ flex: 1 }}>
              {!on ? <span style={{ display: "inline-flex", alignItems: "center", height: 34, fontSize: 13, color: T.soft }}>{t("lib.svcEditorPrimitives.closed", "Closed")}</span> : (
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {ranges.map((r, idx) => (
                    <div key={idx} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <input type="time" value={r.start} disabled={!canWrite} onChange={(e) => setRange(day, idx, "start", e.target.value)} style={ti} />
                      <span style={{ color: T.soft, fontSize: 13 }}>—</span>
                      <input type="time" value={r.end} disabled={!canWrite} onChange={(e) => setRange(day, idx, "end", e.target.value)} style={ti} />
                      {canWrite ? <IconBtn size={32} onClick={() => rmRange(day, idx)}><Icon.x size={13} /></IconBtn> : null}
                      {canWrite && idx === ranges.length - 1 ? <Btn size="sm" variant="link" onClick={() => addRange(day)}>{t("lib.svcEditorPrimitives.time", "+ Time")}</Btn> : null}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
